import axios from "axios";

// Root of the OwnFood API (no version suffix) — used by the socket client too.
export const API_ROOT = (
  process.env.REACT_APP_API_ROOT || "https://api.ownfood.com"
).replace(/\/+$/, "");

// Every REST call in this app goes through /api/v2.
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  `${API_ROOT}/api/v2`;

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true, // Allow cookies and credentials
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem("Token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    try {
      const coordinates = JSON.parse(localStorage.getItem("coordinates"));
      if (coordinates?.lat && coordinates?.lon) {
        config.headers.latitude = coordinates.lat;
        config.headers.longitude = coordinates.lon;
      }
    } catch {
      // Ignore invalid/missing coordinates in localStorage
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/**
 * A 401 from `/auth/*` means "those credentials were wrong", not "your session
 * expired" — bouncing the visitor to the login screen there would wipe a
 * half-finished sign-up on a mistyped OTP. Only a 401 on some *other* endpoint
 * means the stored token is no longer good.
 */
const isAuthEndpoint = (url = "") => /(^|\/)auth\//.test(url);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const onAuthScreen = window.location.pathname.startsWith("/authentication");

    if (status === 401 && !isAuthEndpoint(error.config?.url) && !onAuthScreen) {
      localStorage.removeItem("Token");
      localStorage.removeItem("Token2");
      localStorage.removeItem("UserDetails");
      window.location.href = "/authentication/login";
    }
    return Promise.reject(error);
  },
);

/**
 * The API answers every failure with `{ status: false, message, data? }`.
 * Axios buries that under `error.response.data`, so components that only look
 * at `error.message` used to show "Request failed with status code 400".
 */
export const apiErrorMessage = (error, fallback = "Something went wrong") =>
  error?.response?.data?.message || error?.message || fallback;

const rethrow = (error) => {
  const wrapped = new Error(apiErrorMessage(error));
  wrapped.response = error?.response;
  wrapped.status = error?.response?.status;
  wrapped.original = error;
  throw wrapped;
};

// GET request
export const GET = async (url, config = {}) => {
  try {
    const response = await axiosInstance.get(url, config);
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

// POST request
export const POST = async (url, data = {}, config = {}) => {
  try {
    const response = await axiosInstance.post(url, data, config);
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

// PUT request
export const PUT = async (url, data = {}, config = {}) => {
  try {
    const response = await axiosInstance.put(url, data, config);
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

// PATCH request
export const PATCH = async (url, data = {}, config = {}) => {
  try {
    const response = await axiosInstance.patch(url, data, config);
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

// DELETE request
export const DELETE = async (url, config = {}) => {
  try {
    const response = await axiosInstance.delete(url, config);
    return response.data;
  } catch (error) {
    return rethrow(error);
  }
};

// Export axios instance for advanced usage if needed
export default axiosInstance;
